import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Bell, Cherry, Clover, Dices, Gem, Ship, Star } from "lucide-react";
import { Reel } from "./kit";
import { flyCoins, rainCoins } from "./CoinFly";
import { sfx } from "@/audio";
import { ACC, SHIP, reduced } from "./util";

// One reveal at a time, queued; tap to skip.
//   reveal({ kind: "crate", prize, top, host, boost })   a mystery crate from a trade
//   reveal({ kind: "luck", item: { what, uxd, mult, luck } })   a slot reel for lucky cargo
//   reveal({ kind: "casino", mult, uxd, top })   a Sea Casino spin, mult 0 on a miss
//   reveal({ kind: "win", n, mega })   the big-win banner
export const reveal = (detail) => window.dispatchEvent(new CustomEvent("xf-reveal", { detail }));

export function RevealLayer() {
  const [q, setQ] = useState([]);
  useEffect(() => {
    const on = ({ detail }) => setQ((x) => [...x, { ...detail, id: Math.random() }]);
    window.addEventListener("xf-reveal", on);
    return () => window.removeEventListener("xf-reveal", on);
  }, []);
  const cur = q[0];
  if (!cur) return null;
  const View = { crate: Crate, luck: Luck, casino: Casino, win: Win }[cur.kind];
  return <View key={cur.id} {...cur} done={() => setQ((x) => x.slice(1))} />;
}

const Stage = ({ done, children }) => (
  <motion.div data-testid="reveal" onClick={done} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
    className="reveal-stage fixed inset-0 z-[65] grid cursor-pointer place-items-center overflow-hidden">
    {children}
  </motion.div>
);

// Starts `fn` after `ms`, ends the reveal after `end`.
const useScript = (ms, fn, end, done) =>
  useEffect(() => {
    const r = reduced();
    const a = setTimeout(fn, r ? Math.min(ms, 200) : ms), b = setTimeout(done, r ? end / 2 : end);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

const Burst = ({ color, size = 120 }) => (
  <motion.span className="absolute rounded-full border-2" style={{ width: size, height: size, borderColor: color }}
    initial={{ scale: 0.5, opacity: 0.9 }} animate={{ scale: 2.6, opacity: 0 }} transition={{ duration: 0.7 }} />
);

const Jackpot = () => (
  <div className="hud-panel rounded-full px-6 py-2 text-3xl font-semibold text-x-gold-ink">Jackpot</div>
);

// A crate drops, shakes, bursts open and shows its prize.
function Crate({ prize, top, host, boost, done }) {
  const [open, setOpen] = useState(false);
  useScript(1300, () => {
    setOpen(true);
    sfx("trade");
    setTimeout(() => sfx(top ? "jackpot" : "win"), 700);
    if (top) rainCoins(48); else flyCoins(undefined, prize >= 100 ? 22 : 12);
  }, top ? 4800 : 3000, done);
  return (
    <Stage done={done}>
      <div className="relative flex flex-col items-center gap-4">
        {!open ? (
          <motion.div initial={{ y: -320, scale: 0.8, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 170, damping: 14 }}>
            <div className="shake" style={{ animationDelay: "0.5s" }}>
              <div className="hud-panel relative grid h-28 w-28 place-items-center overflow-hidden rounded-[24px]">
                <span className="absolute inset-y-0 left-1/2 w-4 -translate-x-1/2 bg-x-gold opacity-80" />
                <span className="absolute inset-x-0 top-1/2 h-4 -translate-y-1/2 bg-x-gold opacity-80" />
                <span className="relative text-5xl font-semibold">?</span>
              </div>
            </div>
          </motion.div>
        ) : (
          <>
            <div className="relative grid place-items-center">
              <Burst color={ACC.gold} />
              <motion.div initial={{ scale: 0.3, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 240, damping: 16 }}
                className="coin h-32 w-32">
                <span className="text-center leading-none"><Reel value={prize} className="text-4xl font-semibold" /><span className="block text-xs font-semibold">UXD</span></span>
              </motion.div>
            </div>
            {top && <Jackpot />}
            {host && <motion.span initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3, duration: 0.25 }}
              className="acc-tint rounded-full px-3 py-1 text-sm font-semibold text-x-ink" style={{ "--acc": ACC.green }}>@{host.h} · +{boost}%</motion.span>}
          </>
        )}
      </div>
    </Stage>
  );
}

const SYMS = [Cherry, Gem, "2×", Star, "5×", Bell, "20×", Clover];
const LAND = { "2x": "2×", "5x": "5×", jackpot: "20×" };
const ROW = 64, STOP = 22;

// One reel: a long strip that spins and settles on `target`, showing a symbol above and below it.
function Slot({ target, delay, spin, color }) {
  const strip = useMemo(() => {
    const pick = () => SYMS[Math.floor(Math.random() * SYMS.length)];
    return [...Array.from({ length: STOP }, pick), target, pick()];
  }, [target]);
  const go = spin && !reduced();
  return (
    <div className="reel-mask hud-well relative h-48 w-[76px] overflow-hidden rounded-[16px]">
      <div style={{ transform: `translateY(${spin ? -(STOP - 1) * ROW : 0}px)`, transition: go ? `transform ${1.3 + delay}s cubic-bezier(.12,.8,.2,1)` : "none" }}>
        {strip.map((S, i) => (
          <div key={i} className="grid place-items-center text-4xl font-semibold tabular-nums" style={{ height: ROW }}>
            {typeof S === "string" ? S : <S className="h-9 w-9 text-x-mute" strokeWidth={1.8} />}
          </div>
        ))}
      </div>
      <div className="pointer-events-none absolute inset-x-0 top-1/3 h-16 border-y-2 opacity-70" style={{ borderColor: color }} />
    </div>
  );
}

// Three reels spin and land on `targets`, then show the prize, or that there's none.
function Reels({ title, icon: Icon, targets, color, prize, done }) {
  const [spin, setSpin] = useState(false);
  const [won, setWon] = useState(false);
  const jack = prize?.jack;
  useEffect(() => {
    const a = setTimeout(() => { setSpin(true); sfx("reel"); }, 150), b = setTimeout(() => sfx("reel"), 1350);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, []);
  useScript(reduced() ? 100 : 3100, () => {
    setWon(true);
    sfx(jack ? "jackpot" : prize ? "win" : "lose");
    if (jack) rainCoins(56); else if (prize) flyCoins(undefined, 18);
  }, jack ? 7200 : prize ? 5400 : 4200, done);
  return (
    <Stage done={done}>
      <div className="relative flex flex-col items-center gap-4">
        <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.25 }}
          className="hud-panel rounded-[28px] p-4">
          <div className="mb-3 flex items-center justify-center gap-2 text-base font-semibold"><Icon className="h-5 w-5 text-x-accent" />{title}</div>
          <div className="flex gap-2">
            {targets.map((t, i) => <Slot key={i} target={t} delay={i * 0.55} spin={spin} color={color} />)}
          </div>
        </motion.div>
        {won && (prize ? (
          <>
            {jack && <Jackpot />}
            <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 300, damping: 18 }}
              className="acc-fill flex items-center gap-3 rounded-full px-5 py-2 text-3xl font-semibold" style={{ "--acc": color }}>
              <span>{prize.label}</span>
              <span className="flex items-baseline gap-1">+<Reel value={prize.uxd} /><span className="text-sm">UXD</span></span>
            </motion.div>
          </>
        ) : (
          <motion.div initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.25 }}
            className="hud-panel rounded-full px-5 py-2 text-xl font-semibold text-x-mute" data-testid="reveal-miss">No luck this time</motion.div>
        ))}
      </div>
    </Stage>
  );
}

// Lucky cargo: the reels land on the multiplier.
function Luck({ item, done }) {
  const label = LAND[item.luck], jack = item.luck === "jackpot";
  return <Reels title="Lucky cargo" icon={SHIP[item.what] ?? Ship} targets={[label, label, label]} color={ACC[jack ? "gold" : item.luck === "5x" ? "blue" : "green"]}
    prize={{ label, uxd: item.uxd, jack }} done={done} />;
}

// A Sea Casino spin: three of a kind on a win, three that don't match on a miss.
function Casino({ mult, uxd, top, done }) {
  const [miss] = useState(() => [...SYMS].sort(() => Math.random() - 0.5).slice(0, 3));
  const label = `${mult}×`;
  return <Reels title="Sea Casino" icon={Dices} targets={mult ? [label, label, label] : miss} color={ACC[top ? "gold" : mult >= 5 ? "blue" : mult ? "green" : "grey"]}
    prize={mult ? { label, uxd, jack: top } : null} done={done} />;
}

// The banner for a big haul: it pops in and fades by itself.
function Win({ n, mega, done }) {
  useScript(0, () => { sfx(mega ? "jackpot" : "win"); rainCoins(mega ? 56 : 32); }, mega ? 3800 : 3000, done);
  const s = mega ? 3.8 : 3;
  return (
    <div className="pointer-events-none fixed inset-x-0 top-[22%] z-[65] grid place-items-center px-3">
      <motion.div data-testid="win-banner" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: [0.6, 1.08, 1, 1, 0.95], opacity: [0, 1, 1, 1, 0] }}
        transition={{ duration: s, times: [0, 0.08, 0.16, 0.88, 1] }}
        className="hud-panel rounded-[28px] px-7 py-4 text-center">
        <div className="text-4xl font-semibold text-x-gold-ink sm:text-6xl">{mega ? "Mega win" : "Big win"}</div>
        <div className="money mt-1 flex items-baseline justify-center gap-1 text-3xl font-semibold">+<Reel value={n} /><span className="text-sm">UXD</span></div>
      </motion.div>
    </div>
  );
}
